import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Search, Star, Bot } from 'lucide-react';
import { apiClient } from '../apiClient';
import SelectMenu from '../components/SelectMenu';
import VisibilityIndicator from '../components/VisibilityIndicator';
import { richTextToPlainText } from '../utils/richText';

const PROMPT_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest', description: 'Recently created public prompts' },
  { value: 'oldest', label: 'Oldest', description: 'Oldest public prompts first' },
  { value: 'highest_rated', label: 'Highest Rated', description: 'Top rated prompts first' }
];

const LIST_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest', description: 'Recently created public items' },
  { value: 'oldest', label: 'Oldest', description: 'Oldest public items first' }
];

const INSTRUCTION_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest', description: 'Recently created instructions' },
  { value: 'oldest', label: 'Oldest', description: 'Oldest instructions first' }
];

const PAGE_SIZE = 9;

function PromptLibraryPage() {
  const [view, setView] = useState('prompts');
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [groups, setGroups] = useState({});
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadCategories = async () => {
      setLoading(true);
      try {
        const data = await apiClient.get('/api/library/categories', { resource_type: view });
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    setExpanded({});
    setGroups({});
    loadCategories();
  }, [view]);

  const fetchCategory = useCallback(async (categoryId, page = 0, append = false) => {
    setGroups((prev) => ({
      ...prev,
      [categoryId]: {
        items: prev[categoryId]?.items || [],
        total: prev[categoryId]?.total ?? 0,
        page: prev[categoryId]?.page ?? 0,
        loading: true
      }
    }));
    try {
      const endpoint = view === 'projects'
        ? '/api/library/projects'
        : view === 'folders'
          ? '/api/library/folders'
          : view === 'instructions'
            ? '/api/library/instructions'
            : '/api/library/prompts';
      const data = await apiClient.get(endpoint, {
        category_id: categoryId,
        search,
        sort,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE
      });
      const items = Array.isArray(data.items) ? data.items : [];
      setGroups((prev) => {
        const existing = prev[categoryId]?.items || [];
        const nextItems = append ? [...existing, ...items] : items;
        return {
          ...prev,
          [categoryId]: {
            items: nextItems,
            total: data.total || 0,
            page,
            loading: false
          }
        };
      });
    } catch (err) {
      setGroups((prev) => ({
        ...prev,
        [categoryId]: {
          items: [],
          total: 0,
          page: 0,
          loading: false
        }
      }));
    }
  }, [search, sort, view]);

  useEffect(() => {
    setGroups({});
    Object.keys(expanded).forEach((categoryId) => {
      if (expanded[categoryId]) {
        fetchCategory(categoryId, 0, false);
      }
    });
  }, [expanded, fetchCategory, search, sort]);

  const toggleCategory = (categoryId) => {
    setExpanded((prev) => {
      const nextValue = !prev[categoryId];
      if (nextValue && !groups[categoryId]) {
        fetchCategory(categoryId, 0, false);
      }
      return { ...prev, [categoryId]: nextValue };
    });
  };

  const orderedCategories = useMemo(() => {
    return [...(categories || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [categories]);

  const renderPromptCard = (prompt) => (
    <div
      key={prompt.prompt_id}
      className="card-glass cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/prompts/${prompt.prompt_id}`, {
        state: {
          backTo: '/library',
          breadcrumbs: [{ label: 'Prompt Library', to: '/library' }]
        }
      })}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(`/prompts/${prompt.prompt_id}`, {
            state: {
              backTo: '/library',
              breadcrumbs: [{ label: 'Prompt Library', to: '/library' }]
            }
          });
        }
      }}
    >
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-bold text-lg">{prompt.name}</h3>
        <div className="flex items-center gap-2">
          <VisibilityIndicator visibility={prompt.visibility || 'public'} />
          <span className="badge">{prompt.visibility || 'public'}</span>
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
        {richTextToPlainText(prompt.description) || 'No description'}
      </p>
      <div className="library-card-meta">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Star className="w-4 h-4" />
          {Number(prompt.average_rating ?? 0).toFixed(1)}
          <span className="text-xs text-gray-400">({prompt.rating_count ?? 0})</span>
        </div>
        {prompt.creator && (
          <span className="text-xs text-gray-400">
            {prompt.creator.username || prompt.creator.name || 'Creator'}
          </span>
        )}
      </div>
    </div>
  );

  const headerCopy = view === 'prompts'
    ? 'Explore public prompts by category and creator.'
    : view === 'projects'
      ? 'Browse public projects by category and creator.'
      : view === 'instructions'
        ? 'Discover agent instructions shared by the community.'
        : 'Browse public folders by category and creator.';

  const renderInstructionCard = (instruction) => (
    <div
      key={instruction.instruction_id}
      className="card-glass cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/library/instructions/${instruction.instruction_id}`, {
        state: {
          backTo: '/library',
          breadcrumbs: [{ label: 'Library', to: '/library' }]
        }
      })}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(`/library/instructions/${instruction.instruction_id}`);
        }
      }}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <Bot className="w-4 h-4 text-cyan-400" />
          </div>
          <h3 className="font-bold text-lg">{instruction.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          {instruction.live_enabled && (
            <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-300">Live</span>
          )}
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
        {instruction.description || 'No description'}
      </p>
      <div className="library-card-meta">
        {instruction.creator && (
          <span className="text-xs text-gray-400">
            by {instruction.creator.username || instruction.creator.name || 'Creator'}
          </span>
        )}
        {instruction.tags?.length > 0 && (
          <div className="flex gap-1">
            {instruction.tags.slice(0, 2).map((tag, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded bg-white/10 text-gray-400">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="page-header">
          <div>
            <h1 className="text-3xl font-bold">Prompt Library</h1>
            <p className="text-gray-400">{headerCopy}</p>
          </div>
          <div className="page-header-actions">
            <div className="library-toggle">
              {['prompts', 'projects', 'folders', 'instructions'].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`library-toggle-btn ${view === value ? 'active' : ''}`}
                  onClick={() => setView(value)}
                >
                  {value === 'instructions' ? 'Instructions' : value.charAt(0).toUpperCase() + value.slice(1)}
                </button>
              ))}
            </div>
            <div className="search-field">
              <Search className="w-4 h-4" />
              <input
                type="text"
                placeholder={`Search ${view === 'instructions' ? 'instructions' : view} or creators`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="sort-field">
              <div className="sort-label">Sort</div>
              <SelectMenu
                value={sort}
                options={view === 'prompts' ? PROMPT_SORT_OPTIONS : view === 'instructions' ? INSTRUCTION_SORT_OPTIONS : LIST_SORT_OPTIONS}
                placeholder="Sort"
                onChange={setSort}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : orderedCategories.length === 0 ? (
          <div className="card-glass text-center py-12">
            <p className="text-gray-400 mb-4">No public {view} available yet.</p>
            <button className="btn-primary" onClick={() => navigate('/profile')}>
              Back to Profile
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {orderedCategories.map((category) => {
              const categoryId = category.category_id;
              const group = {
                items: [],
                total: category?.count ?? 0,
                page: 0,
                loading: false,
                ...(groups[categoryId] || {})
              };
              const items = Array.isArray(group.items) ? group.items : [];
              const isOpen = !!expanded[categoryId];
              const canLoadMore = items.length < (group.total || 0);

              return (
                <section key={categoryId} className="library-section">
                  <button
                    type="button"
                    className="library-header"
                    onClick={() => toggleCategory(categoryId)}
                  >
                    <div>
                      <div className="library-title">{category.name}</div>
                      <div className="library-sub">{category.count} public {view}</div>
                    </div>
                    <ChevronDown className={`w-5 h-5 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="library-body">
                      {group.loading && items.length === 0 ? (
                        <div className="flex justify-center py-8">
                          <div className="spinner"></div>
                        </div>
                      ) : items.length === 0 ? (
                        <div className="card-glass text-center py-6">
                          <p className="text-gray-400">No {view} in this category yet.</p>
                        </div>
                      ) : (
                        <>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                            {view === 'prompts' && items.map(renderPromptCard)}
                            {view === 'instructions' && items.map(renderInstructionCard)}
                            {(view === 'projects' || view === 'folders') && items.map((item) => (
                              <div
                                key={view === 'projects' ? item.project_id : item.folder_id}
                                className="card-glass cursor-pointer"
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(
                                  view === 'projects'
                                    ? `/projects/${item.project_id}`
                                    : `/projects/${item.project_id}/folders/${item.folder_id}`
                                )}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    navigate(
                                      view === 'projects'
                                        ? `/projects/${item.project_id}`
                                        : `/projects/${item.project_id}/folders/${item.folder_id}`
                                    );
                                  }
                                }}
                              >
                                <div className="flex justify-between items-start mb-3">
                                  <h3 className="font-bold text-lg">{item.name}</h3>
                                  <div className="flex items-center gap-2">
                                    <VisibilityIndicator visibility={item.visibility || 'public'} />
                                    <span className="badge">{item.visibility || 'public'}</span>
                                  </div>
                                </div>
                                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                                  {richTextToPlainText(item.description) || 'No description'}
                                </p>
                                <div className="library-card-meta">
                                  <span className="text-xs text-gray-400">
                                    {item.creator?.username || item.creator?.name || 'Creator'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          {canLoadMore && (
                            <button
                              className="btn-secondary btn-compact library-load"
                              onClick={() => fetchCategory(categoryId, group.page + 1, true)}
                            >
                              Load more
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PromptLibraryPage;
